import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import { misc, neutral } from "app/themes/colors";
import {
  ContactNameLink,
  OpenContactButton
} from "features/calls/rolodex/contact-link.component";
import type { CallContact } from "services/dialstack";
import { useGetPatientQuery } from "services/patients";

import { selectAttachmentOverlay } from "../call-attachments.slice";
import { displayNumber } from "../call-display.util";
import {
  callerDisplayName,
  callerSubtitle
} from "../unknown-caller/caller-identity.util";
import UnknownCallerActions from "../unknown-caller/unknown-caller-actions.component";
import type { UnknownCallerActions as Actions } from "../unknown-caller/use-unknown-caller-actions.hook";
import { INCOMING_CALL_PATIENTS_QUERY } from "../use-incoming-call-patients.hook";
import { LILAC, PLUM_600, PLUM_900 } from "./dialstack-softphone-widget.styles";
import SoftphonePatientHero from "./softphone-patient-hero.component";

export type UnknownCaller = {
  backendCallId: number | null;
  remoteNumber: string;
  fromName: string | null;
  contact: CallContact | null;
  // From the pop: the clinic the call rang for, which a saved lead files under.
  clinicId: number | null;
  // Set when a colleague transferred this call to us.
  transferredBy?: string | null;
};

type IncomingCallUnknownCallerCardProps = {
  caller: UnknownCaller;
  // Which live call this is ("Incoming call" / "Current call" / "On hold").
  // Null once the SDK call has ended and the card is showing the recent
  // caller; that state gets a dismiss control.
  liveLabel: string | null;
  // Absent on the extra cards shown when several unknown callers are live at
  // once: the dialogs are single-instance, so only the first card files calls.
  actions?: Actions;
  onDismiss?: () => void;
  // Rendered inside the call pop, whose header already says which call this
  // is: the label row is kept only for who transferred it (ENG-2226).
  inPop?: boolean;
};

const ACTION_SX = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  px: 1.25,
  py: 0.25,
  minWidth: 0,
  color: PLUM_600,
  borderColor: LILAC,
  "&:hover": { borderColor: PLUM_600, bgcolor: misc.lightLavender }
} as const;

// Swaps to the full patient hero once the call has been attached to a
// patient (Quick Add or attach-to-existing).
function AttachedPatientHero({ patientId }: { patientId: number }) {
  const { data: patient } = useGetPatientQuery({
    patientId,
    query: INCOMING_CALL_PATIENTS_QUERY
  });
  if (!patient) {
    return (
      <Stack spacing={0.5} sx={{ py: 0.5 }}>
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="45%" />
      </Stack>
    );
  }
  return (
    <Box data-testid="unknown-caller-attached-patient">
      <SoftphonePatientHero patient={patient} caption="Attached to this call" />
    </Box>
  );
}

// Inbound call from a number that matched no patient: the carrier caller-ID
// name (always labelled as such — it is the line's registered name, not a
// verified identity), the contact when the number belongs to one, and the
// ways to file the call. Actions and dialogs come from the widget-level hook
// so they outlive this card.
export default function IncomingCallUnknownCallerCard({
  caller,
  liveLabel,
  actions,
  onDismiss,
  inPop = false
}: IncomingCallUnknownCallerCardProps) {
  const overlay = useAppSelector(selectAttachmentOverlay);
  const attached =
    caller.backendCallId !== null ? overlay[caller.backendCallId] : undefined;
  const contact = attached ? attached.contact : caller.contact;
  const attachedPatientId = attached?.patient?.id ?? null;

  const identity = {
    contact,
    fromName: caller.fromName,
    number: caller.remoteNumber
  };
  const name = callerDisplayName(identity);
  const subtitle = callerSubtitle(identity);
  const showsNumberAsName = !contact && !caller.fromName?.trim();

  return (
    <Box data-testid="incoming-call-unknown-caller">
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        {(!inPop || caller.transferredBy) && (
          <Typography
            sx={{ fontSize: 12, color: neutral[800], flex: 1, pt: 0.25 }}
          >
            {inPop
              ? `Transferred by ${caller.transferredBy}`
              : (liveLabel ?? "Recent call · ended")}
            {!inPop && liveLabel && caller.transferredBy
              ? ` · Transferred by ${caller.transferredBy}`
              : ""}
          </Typography>
        )}
        {liveLabel === null && (
          <IconButton
            aria-label="Dismiss recent caller"
            size="small"
            onClick={onDismiss}
            sx={{ mr: -0.5, mt: -0.5, color: neutral[600] }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Stack>
      {attachedPatientId !== null ? (
        <AttachedPatientHero patientId={attachedPatientId} />
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 0.5 }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: LILAC }}>
              <BusinessIcon sx={{ fontSize: 18, color: PLUM_600 }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {contact ? (
                <ContactNameLink
                  contact={contact}
                  fontSize={15}
                  fontWeight={700}
                  color={PLUM_600}
                  testId="unknown-caller-contact-link"
                />
              ) : (
                <Typography
                  noWrap
                  sx={{ fontSize: 15, fontWeight: 700, color: PLUM_900 }}
                  data-testid="unknown-caller-name"
                >
                  {name}
                </Typography>
              )}
              {!showsNumberAsName && (
                <Typography
                  noWrap
                  sx={{ fontSize: 12, color: neutral[700] }}
                  data-testid="unknown-caller-subtitle"
                >
                  {contact
                    ? subtitle.text
                    : `${displayNumber(caller.remoteNumber)} · Caller ID`}
                </Typography>
              )}
              {showsNumberAsName && (
                <Typography
                  sx={{ fontSize: 12, color: neutral[600] }}
                  data-testid="unknown-caller-subtitle"
                >
                  {subtitle.text}
                </Typography>
              )}
            </Box>
          </Stack>
          {actions?.recovery && (
            <Stack
              spacing={0.75}
              data-testid="unknown-caller-recovery"
              sx={{
                mt: 1,
                p: 1,
                borderRadius: "10px",
                bgcolor: misc.lightYellow
              }}
            >
              <Typography sx={{ fontSize: 12, color: misc.warningText }}>
                {actions.recovery.patientName} was added, but this call could
                not be attached to them.
              </Typography>
              <Stack direction="row" spacing={0.75}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={ACTION_SX}
                  disabled={actions.isAttaching}
                  onClick={actions.retryAttach}
                  data-testid="unknown-caller-retry-attach"
                >
                  Retry attach
                </Button>
                <Button
                  size="small"
                  sx={ACTION_SX}
                  onClick={actions.dismissRecovery}
                >
                  Dismiss
                </Button>
              </Stack>
            </Stack>
          )}
          {contact && (
            <Stack direction="row" sx={{ mt: 1 }}>
              <OpenContactButton contact={contact} sx={ACTION_SX} />
            </Stack>
          )}
          {actions && (
            <UnknownCallerActions
              actions={actions}
              contact={contact}
              size="small"
              buttonSx={ACTION_SX}
              sx={{ mt: 1 }}
            />
          )}
        </>
      )}
    </Box>
  );
}
